import React, { useEffect, useMemo, useState } from 'react'
import { cn, getUserType, toCamelCase } from '@utils/safeTs'
import styles from './FlussoPersonalizzazioneCaricamentoLoghiPosizione.module.sass'
import { Button, FormField, FormTextarea, StatoOrdine, FormFile, PosizionePersonalizzazioneOrdineStatus, MyAccountImagePreview, CronologiaButton, Alert } from '@components/atoms'
import { ChevronDown, Upload, Bin, Info, CheckCircle } from '@components/icons'
import { OrdineDetailType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneType, RootStateType } from '@utils/types'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import MyAccountStatusLogModal from '../MyAccountStatusLogModal/MyAccountStatusLogModal'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { api } from '@utils/index'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import FlussoPersonalizzazionePosizione from '../FlussoPersonalizzazionePosizione/FlussoPersonalizzazionePosizione'
import FlussoPersonalizzazionePosizioneConfigurazione from '../FlussoPersonalizzazionePosizioneConfigurazione/FlussoPersonalizzazionePosizioneConfigurazione'
import { useRouter } from 'next/router'
import { EDITOR_FILE_EXTENSIONS } from '@utils/editor-bozze'


interface Props {
  posizione: PosizionePersonalizzazioneType
  personalizzazione: PersonalizzazioneOrdineType
  setPosizione?:  (v: PosizionePersonalizzazioneType) => void
  setOrdine: (p: OrdineDetailType) => void
  setStatus: ((v: '' | 'unsaved' | 'completed') => void)
  personalizzazioniOpen?: number[]
  setPersonalizzazioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  posizioniOpen: number[]
  setPosizioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  className?: string
}

const FlussoPersonalizzazioneCaricamentoLoghiPosizione = (props: Props) => {

  const { 
    posizione,
    personalizzazione,
    setPosizione,
    setOrdine,
    setStatus,
    posizioniOpen,
    setPosizioniOpen,
    className = '',
  } = props
  
  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)

  // cliente può modificare i loghi solo se lo stato è in LOGO_DA_CARICARE, admin e commerciale (e admin sg che sarebbe cliente) sempre (supponendo che sappiano cosa stanno facendo)
  const getIsEditable = (pos, userType) => ((['LOGO_DA_CARICARE'].includes(pos.statoPosizionePersonalizzazione) || utente?.is_staff_sg) && ['cliente'].includes(userType)) || ['admin', 'commerciale', undefined].includes(userType)
  const isEditable = useMemo(() => getIsEditable(posizione, userType), [userType, posizione])
  const lastLogoLog = useMemo(() => posizione.log.filter(l => l.stato === 'BOZZA_DA_CARICARE')?.sort((a, b) => a.created < b.created ? -1 : 1)?.[0], [posizione.log])

  const [logOpen, setLogOpen] = useState(false)
  const [edited, setEdited] = useState(false)

  const status = useMemo(() => edited ? 'unsaved' : '', [edited])
  const allowedExtensions = useMemo(
    () => (posizione.tecnica?.estensioniAmmesse || EDITOR_FILE_EXTENSIONS)
      .map((extension) => extension?.trim()?.toLowerCase())
      .filter(Boolean),
    [posizione.tecnica?.estensioniAmmesse]
  )
  const logoAccept = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(',') : undefined
  const formatiConsentitiLabel = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(', ') : ""

  const [values, setValues] = useState({
    note: lastLogoLog?.note || '',
    logo: posizione.logo || undefined,
    fileLogo: undefined,
  })

  // Salvataggio logo
  const save = (v) => {
    let formData = new FormData()
    Object.keys(v).forEach((k) => {
      formData.append(k, v[k])
    })
    api
      .put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${posizione.id}/update_logo/`, formData, {
        params: {
          ...(hashParam ? { hash: hashParam } : {}),
        },
      })
      .then((res) => {
        const newOrdine = toCamelCase(res.data) as OrdineDetailType
        setOrdine(newOrdine)
        setEdited(false)
        displayNotification(
          `posizione_aggiornata_${posizione.id}`, 
          t("Logo per {0} confermato", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'success'
        )
      })
      .catch((res) => {
        console.log('ko', res)
        displayNotification(
          `posizione_aggiornata_error_${posizione.id}`, 
          t("Errore durante l'aggiornamento della posizione {0}", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'error'
        )
      })
  }

  useEffect(() => {
    setStatus(status)
  }, [status])

  return (
    <FlussoPersonalizzazionePosizione 
      status={status}
      posizione={posizione}
      posizioniOpen={posizioniOpen}
      setPosizioniOpen={setPosizioniOpen}
      userType={userType}
      className={cn(styles.root, posizioniOpen.includes(posizione.id) ? styles['root--open'] : '', isEditable ? styles['root--editable'] : '', className)}
    >
      <FlussoPersonalizzazionePosizioneConfigurazione posizione={posizione} personalizzazione={personalizzazione} />

      <div className={styles.row}>
        <div className={cn(styles.section, styles['section--logo'])}>
          <p className={styles.sectionTitle}>{t('Logo')}</p>
          <div className={styles.logo}>
            <MyAccountImagePreview 
              url={values.logo?.url} 
              name={values.logo?.name}
              previewName={t("logo")}
              onRemove={() => {
                setValues(prev => ({
                  ...prev,
                  logo: null,
                  fileLogo: null,
                }))
                setEdited(true)
              }} 
              className={styles.logoPreview}
            />
            <div className={styles.logoForm}>
              {values.fileLogo || values.logo ? (
                <div>
                  <div className={styles.logoValue}>
                    <div className={styles.logoFilename}>{values.logo.name}</div>
                    {isEditable && (
                      <Button 
                        variant="ghost" 
                        label={t("Rimuovi")} 
                        ariaLabel={t("Rimuovi nuovo logo")} 
                        icon={<Bin />}
                        icon_pos='right'
                        size="small"
                        colorVariant={'danger'} 
                        onClick={() => {
                          setValues(prev => ({
                            ...prev,
                            logo: null,
                            fileLogo: null,
                          }))
                          setEdited(true)
                        }}
                        className={styles.logoRemoveBig}
                      />
                    )}
                  </div>
                  <p className={styles.helperText}>
                    {t('Formati consentiti: {0}', [formatiConsentitiLabel])}
                  </p>
                </div>
              ) : (
                isEditable ? (
                  <div className={styles.uploadBox}>
                    <FormField className={styles.uploadButton}>
                      <FormFile
                        id={`${posizione.id}_logo`}
                        name="fileLogo"
                        // value={values.fileLogo}
                        onChange={(event) => {
                          setValues(prev => ({
                            ...prev,
                            logo: event.target.files?.[0] ? {
                              url: URL.createObjectURL(event.target.files?.[0]),
                              name: event.target.files?.[0]?.name
                            } : null,
                            fileLogo: event.target.files?.[0],
                          }))
                          setEdited(true)
                        }}
                        variant={'button'}
                        accept={logoAccept}
                        buttonProps={{ 
                          label: t('Carica logo'), 
                          variant: 'secondary', 
                          size: 'small', 
                          icon: <Upload />, 
                          iconPos: 'left' 
                        }}
                      />
                    </FormField>
                    <p className={styles.helperText}>
                      {t('Formati consentiti: {0}', [formatiConsentitiLabel])}
                    </p>
                  </div>
                ) : <></>
              )}
            </div>
          </div>
        </div>
            
        <div className={cn(styles.section, styles['section--note'])}>
          <p className={styles.sectionTitle}>
            <span>{t('Nota')}</span>
            <CronologiaButton onClick={() => setLogOpen(true)} />
          </p>
          <div>
            {isEditable ? (
              <div className={styles.notaForm}>
                <FormField className={styles.notaField}>
                  <FormTextarea
                    id={`${posizione.id}_nota_caricamento`}
                    placeholder={isEditable && (!values.logo || edited) ? t("Aggiungi una nota per il reparto grafico (facoltativa)...") : ''}
                    name="note"
                    value={values.note}
                    disabled={values.logo && !edited}
                    onChange={(e) => setValues(prev => ({...prev, note: e.target.value}))}
                  />
                </FormField>
                <div className={styles.ctaBox}>
                  {!edited && values.logo ? (
                    <Alert 
                      variant="success"
                      styleVariant='basic'
                      icon={<CheckCircle />}
                      placeholder={t("File caricato correttamente")}
                      size="sm"
                      className={styles.successSave}
                    /> 
                  ) : (
                    <>
                      <Button 
                        variant="primary" 
                        size="medium" 
                        label={t('Conferma logo')}
                        disabled={!edited || !values.fileLogo}
                        onClick={() => save(values)}
                      />
                      
                      <Alert 
                        variant="muted" 
                        styleVariant='basic' 
                        icon={<Info />} 
                        placeholder={t("Carica il logo per abilitare il salvataggio e procedere")} 
                        className={styles.alertSave} 
                        size="xs"
                      />
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.formValue}>
                {posizione.log?.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length ? (
                  posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.[posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length - 1].note
                ) : '-'}
              </div>
            )}
          </div>
        </div>
      </div>
      <MyAccountStatusLogModal id={`status_log_modal_${posizione.id}`} open={logOpen} onClose={() => setLogOpen(false)} posizione={posizione} userType={userType} />
    </FlussoPersonalizzazionePosizione>
  )
}

export default FlussoPersonalizzazioneCaricamentoLoghiPosizione
